/**
 * The pour's weather station readings (GET /pour/{id}/weather), oldest
 * first, each [epoch ms UTC, temp °C, humidity %RH, wind km/h]. Each bump of
 * `refreshSignal` asks only for the readings after the newest one held.
 * Without a pour id nothing is fetched.
 */
import { useEffect, useRef, useState } from 'react';
import { poursAPI, type WeatherSeries } from '../../services/pours';
import { apiErrorText } from '../../utils/apiError';

export type WeatherPoint = WeatherSeries['points'][number];

export interface WeatherPoints {
  points: WeatherPoint[];
  /** True until the first reply for this pour. */
  loading: boolean;
  error: string | null;
}

export const useWeatherPoints = (pourId?: string | null, refreshSignal?: number): WeatherPoints => {
  const [points, setPoints] = useState<WeatherPoint[]>([]);
  const [loading, setLoading] = useState(!!pourId);
  const [error, setError] = useState<string | null>(null);
  const pointsRef = useRef<WeatherPoint[]>([]);
  const loadedPour = useRef<string | null>(null);

  useEffect(() => {
    if (!pourId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fresh = loadedPour.current !== pourId;
    const held = fresh ? [] : pointsRef.current;
    const since = held.length > 0 ? held[held.length - 1][0] : undefined;
    if (fresh) setLoading(true);
    poursAPI
      .getPourWeather(pourId, since)
      .then((reply) => {
        if (cancelled) return;
        const next = since === undefined ? reply.points : [...held, ...reply.points];
        pointsRef.current = next;
        loadedPour.current = pourId;
        setPoints(next);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(apiErrorText(e, 'Could not load the weather readings.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pourId, refreshSignal]);

  return { points, loading, error };
};
